import { useMemo, useState } from "react";
import { useParams } from "react-router";
import _ from "lodash";

import { ArrowDropDownIcon } from "icons";
import theme from "theme";
import { colors } from "theme/palette";

import ButtonDropdown from "components/ButtonDropdown";
import { MenuItem } from "components/Menu";
import PageHeader from "components/PageHeader";
import Paper from "components/Paper";
import Skeleton from "components/Skeleton";
import Stack from "components/Stack";
import Text from "components/Text";
import { usePageTitle } from "components/usePageTitle";

import {
  IFinancingEvent,
  useDealDetails,
  useFinancingEvents,
} from "entities/Deal/sdk";

import financingEventAndTransactionIcon from "./assets/financing_event.svg";
import { EMPTY_VALUES, ESortingType } from "./constants";
import FinancingEventBox from "./FinancingEventBox";
import FinancingEventModifyDialogButton from "./FinancingEventModifyDialogButton";
import HelperBox from "./HelperBox";

const FinancingEvents = () => {
  const [sortingType, setSortingType] = useState<ESortingType>(
    ESortingType.CHRONOLOGICAL
  );
  const params = useParams<{
    dealId: string;
  }>();
  const dealId = Number(params.dealId);

  const { data: deal } = useDealDetails({ dealId });

  usePageTitle(
    deal?.name
      ? `Builders Patch: ${deal.name} - Financing events`
      : "Builders Patch: Financing events"
  );

  const { data: financingEvents, isLoading: isLoadingFinancingEvents } =
    useFinancingEvents({
      dealId,
    });

  const sortedFinancingEvents = useMemo(() => {
    if (!_.isNil(financingEvents)) {
      if (sortingType === ESortingType.LATEST_FIRST) {
        return [...financingEvents].reverse();
      }
      if (sortingType === ESortingType.CHRONOLOGICAL) {
        return financingEvents;
      }
    }
  }, [financingEvents, sortingType]);

  return (
    <Paper
      sx={{
        padding: theme.spacing(0, 4, 1, 4),
      }}
    >
      <Stack gap={3}>
        <PageHeader
          title="Financing events"
          subTitle={
            <Text variant="text2">
              Manage all major events of funding for this project that involves
              a closing process, or multiple simultaneous closing processes.
            </Text>
          }
          actions={
            <FinancingEventModifyDialogButton
              financingEventId={null}
              initialValues={EMPTY_VALUES}
            />
          }
        />
        {_.isNil(financingEvents) || isLoadingFinancingEvents ? (
          <Stack>
            <Stack spacing={2}>
              <Skeleton width={200} />
              {_.range(5).map((index) => (
                <Skeleton variant="rectangular" height={100} key={index} />
              ))}
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Stack
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Text variant="text2" color={colors.gray60}>
                {financingEvents?.length} financing event
                {financingEvents?.length === 1 ? "" : "s"} created
              </Text>
              <Stack
                flexDirection="row"
                alignItems="center"
                justifyContent="flex-end"
                gap={1}
                flexGrow={1}
              >
                <Text variant="text2" color={colors.gray60} noWrap>
                  Sort by:
                </Text>
                <ButtonDropdown
                  icon={<ArrowDropDownIcon />}
                  label={sortingType}
                  variant="outlined"
                  color="secondary"
                  keepMenuMounted={true}
                  sx={{
                    padding: theme.spacing(0, 1),
                    "& .MuiButton-endIcon": {
                      margin: 0,
                    },
                    maxWidth: "fit-content",
                    alignSelf: "center",
                    justifySelf: "flex-end",
                  }}
                >
                  <MenuItem
                    onClick={() => setSortingType(ESortingType.CHRONOLOGICAL)}
                  >
                    <Text variant="text2">Closing date: descending</Text>
                  </MenuItem>
                  <MenuItem
                    onClick={() => setSortingType(ESortingType.LATEST_FIRST)}
                  >
                    <Text variant="text2">Closing date: ascending</Text>
                  </MenuItem>
                </ButtonDropdown>
              </Stack>
            </Stack>
            <Stack spacing={2} marginTop={1}>
              {sortedFinancingEvents?.map((event: IFinancingEvent) => (
                <FinancingEventBox event={event} key={event.id} />
              ))}
            </Stack>
          </Stack>
        )}
        <Stack flexDirection="row" gap={2}>
          <HelperBox
            icon={financingEventAndTransactionIcon}
            heading="What are financing events?"
            description="Financing events are created to track the movement of money and
                consist of individual transactions needed to finance or acquire
                an asset: loans & other forms of funding, the exact terms of
                funding, expected returns, etc. are all documented within
                transactions."
          />
          <HelperBox
            icon={financingEventAndTransactionIcon}
            heading="What are transactions?"
            description="Transactions track the exact amount invested in a project along
                with the type of financing used, the terms & conditions were set
                up, and current status of the transaction. Creating transactions
                on the deal level will create an overview of all transactions
                across your portfolio."
          />
        </Stack>
      </Stack>
    </Paper>
  );
};

export default FinancingEvents;
